/** @jsxImportSource preact */
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import * as m from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { FORM_STYLES, formVars } from "../form/form-styles";
import {
  CheckIcon,
  ChevronRightIcon,
  PackageIcon,
  SpinnerIcon,
} from "../icons";
import { SR_ONLY } from "../sr-only";
import type { WidgetTokens } from "../theme";

/** 列表里的一笔订单。日期、商品名原文由后端给，挂件不翻译、不换算。 */
export interface WidgetOrderSummary {
  /** 下单日期，已按买家语言格式化。 */
  date: string;
  id: string;
  imageUrl?: string;
  /** 第一件商品名。 */
  itemTitle: string;
  /** 订单号，含 #。 */
  number: string;
  /** 除第一件外还有几件；0 不显示。 */
  moreItems: number;
}

export interface OrderListCardProps {
  readOnly?: boolean;
  disabled?: boolean;
  /** 正在加载下一页：按钮禁用，底部出两行骨架。 */
  hasMore?: boolean;
  /** 首次加载：标题还在，空文案不出，井里是骨架行。 */
  loading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  onSelect?: (id: string) => void;
  orders: WidgetOrderSummary[];
  /** 已选中的一笔：留在列表里，订单结果卡作为下一条消息出现。 */
  selectedId?: string;
  tokens: WidgetTokens;
  /** 买家名下的订单总数；大于 orders.length 时出 Show N more。 */
  total?: number;
}

/** 列表区域最高 300px，在井里滚动，Show N more 固定在卡片底部。 */
const LIST_MAX_HEIGHT = 300;

const ORDER_LIST_CARD_STYLES = `
.gn-orders { max-width:320px; }
.gn-orders-head { align-items:center; }
.gn-orders-list { gap:0; padding:6px; }
.gn-orders-list:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; }
.gn-orders-row { flex:none; box-sizing:border-box; display:flex; align-items:center; gap:10px; width:100%; padding:6px 8px; border:0; border-radius:var(--gn-form-r-cell); background:transparent; color:inherit; font:inherit; text-align:left; cursor:pointer; transition:background .15s; }
.gn-orders-row:hover:not([data-current]):not([data-readonly]) { background:var(--gn-form-chip); }
.gn-orders-row[data-readonly] { cursor:default; }
.gn-orders-row:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:-2px; }
.gn-orders-row[data-current] { background:var(--gn-form-answer); color:var(--gn-form-answer-fg); }
.gn-orders-thumb { display:flex; align-items:center; justify-content:center; flex:none; box-sizing:border-box; width:40px; height:40px; border-radius:var(--gn-form-r-cell); background:var(--gn-form-chip); color:var(--gn-form-quiet); object-fit:cover; }
.gn-orders-text { flex:1 1 0; min-width:0; display:flex; flex-direction:column; gap:2px; }
.gn-orders-line { display:flex; align-items:baseline; gap:6px; min-width:0; }
.gn-orders-number { flex:none; font-size:13px; font-weight:800; font-variant-numeric:tabular-nums; }
.gn-orders-date { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:12px; color:var(--gn-form-quiet); }
.gn-orders-summary { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:12px; color:var(--gn-form-quiet); }
.gn-orders-row[data-current] .gn-orders-date, .gn-orders-row[data-current] .gn-orders-summary { color:inherit; opacity:.75; }
.gn-orders-row[data-current] .gn-orders-thumb { background:color-mix(in srgb, var(--gn-form-answer-fg) 16%, transparent); color:inherit; }
.gn-orders-go { display:flex; flex:none; color:var(--gn-form-faint); }
.gn-orders-row[data-current] .gn-orders-go { color:inherit; }
.gn-orders-skeleton { display:flex; flex-direction:column; }
.gn-orders-skeleton-row { display:flex; align-items:center; gap:10px; padding:6px 8px; }
.gn-orders-bone { display:block; flex:none; background:var(--gn-form-chip); }
.gn-orders-skeleton-text { display:flex; flex:1 1 0; flex-direction:column; gap:6px; }
.gn-orders-more { align-self:center; min-height:36px; }
.gn-orders-more[disabled] { cursor:default; opacity:.7; }
`;

function OrderRow({
  readOnly,
  disabled,
  onSelect,
  order,
  selected,
}: {
  readOnly?: boolean;
  disabled?: boolean;
  onSelect?: (id: string) => void;
  order: WidgetOrderSummary;
  selected: boolean;
}) {
  const [failedImageUrl, setFailedImageUrl] = useState<string>();
  const summary =
    order.moreItems > 0
      ? `${order.itemTitle} ${m.widget_orders_more_items({ count: order.moreItems })}`
      : order.itemTitle;
  const Row = readOnly ? "div" : "button";
  return (
    <Row
      aria-current={selected ? "true" : undefined}
      disabled={readOnly ? undefined : disabled || !onSelect}
      class="gn-orders-row"
      data-current={selected ? "" : undefined}
      data-readonly={readOnly ? "" : undefined}
      onClick={
        readOnly
          ? undefined
          : () => {
              onSelect?.(order.id);
            }
      }
      type={readOnly ? undefined : "button"}
    >
      {order.imageUrl && order.imageUrl !== failedImageUrl ? (
        <img
          alt=""
          class="gn-orders-thumb"
          src={order.imageUrl}
          onError={() => {
            setFailedImageUrl(order.imageUrl);
          }}
        />
      ) : (
        <span aria-hidden="true" class="gn-orders-thumb">
          <PackageIcon size={18} strokeWidth={1.8} />
        </span>
      )}
      <span class="gn-orders-text">
        <span class="gn-orders-line">
          {/* 读屏读「Order #1052」，画面上只写 #1052。 */}
          <span style={SR_ONLY}>
            {m.widget_orders_order_number({ number: order.number })}
          </span>
          <span aria-hidden="true" class="gn-orders-number">
            {order.number}
          </span>
          <span class="gn-orders-date">{order.date}</span>
        </span>
        <span class="gn-orders-summary">{summary}</span>
      </span>
      {!readOnly && (
        <span class="gn-orders-go">
          {selected ? (
            <CheckIcon size={16} strokeWidth={2.4} />
          ) : (
            <ChevronRightIcon size={16} />
          )}
        </span>
      )}
    </Row>
  );
}

function SkeletonRows({ count = 2 }: { count?: number }) {
  return (
    <div aria-hidden="true" class="gn-orders-skeleton">
      {Array.from({ length: count }, (_, row) => (
        <div class="gn-orders-skeleton-row" key={row}>
          <span
            class="gn-orders-bone"
            style={{
              borderRadius: "var(--gn-form-r-cell)",
              height: 40,
              width: 40,
            }}
          />
          <span class="gn-orders-skeleton-text">
            <span
              class="gn-orders-bone"
              style={{
                borderRadius: "var(--gn-form-r-pill)",
                height: 10,
                width: "40%",
              }}
            />
            <span
              class="gn-orders-bone"
              style={{
                borderRadius: "var(--gn-form-r-pill)",
                height: 8,
                width: "70%",
              }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * 订单列表卡（PRD 2.1）：买家有多笔订单却没说看哪一笔时出这张。白色大圆角卡，
 * 卡头只有标题，不出订单总数和查询时间（GON-352）；订单放进内凹井，每行一个订单（缩略图、订单号、日期、商品摘要），
 * 悬停是白色胶囊，选中的一笔是墨黑实心胶囊并留在列表里，订单结果卡接在下一条消息。
 * 按下单时间倒序，整行是一个按钮；没加载完时底部是居中的 Show N more 文字按钮。
 * 只有一笔订单时不出列表，直接出订单结果卡。
 */
export function OrderListCard({
  readOnly = false,
  disabled = false,
  hasMore: morePages = false,
  loading = false,
  loadingMore = false,
  onLoadMore,
  onSelect,
  orders,
  selectedId,
  tokens: t,
  total,
}: OrderListCardProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      setScrollable(list.scrollHeight > list.clientHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const child of list.children) observer.observe(child);
    return () => {
      observer.disconnect();
    };
  }, [orders, loading, loadingMore]);
  const remaining = total === undefined ? 0 : total - orders.length;
  const hasMore =
    !loading && (remaining > 0 || morePages) && onLoadMore !== undefined;
  return (
    <section
      aria-label={m.widget_orders_title()}
      class="gn-card gn-orders"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${ORDER_LIST_CARD_STYLES}`}</style>
      <div class="gn-card-head gn-orders-head">
        <h3 class="gn-card-title">{m.widget_orders_title()}</h3>
      </div>
      <div
        ref={listRef}
        // 可滚动区域要能被键盘聚焦，否则键盘用户看不到列表后半段。
        aria-label={m.widget_orders_list_label()}
        class="gn-well gn-orders-list"
        role="group"
        style={{
          maxHeight: LIST_MAX_HEIGHT,
          overflowY: "auto",
          overscrollBehavior: scrollable ? "contain" : "auto",
          scrollbarColor: "var(--gn-form-line) transparent",
          scrollbarWidth: "thin",
        }}
        tabIndex={0}
      >
        {orders.map((order) => (
          <OrderRow
            readOnly={readOnly}
            key={order.id}
            disabled={disabled || loadingMore}
            onSelect={onSelect}
            order={order}
            selected={order.id === selectedId}
          />
        ))}
        {orders.length === 0 && !loading && (
          <p class="gn-caption">{m.widget_orders_readable_empty()}</p>
        )}
        {loading && <SkeletonRows count={3} />}
        {loadingMore && !loading && <SkeletonRows />}
      </div>
      {hasMore && (
        <button
          class="gn-btn gn-orders-more"
          data-variant="ghost"
          disabled={disabled || loadingMore}
          onClick={onLoadMore}
          type="button"
        >
          {loadingMore && <SpinnerIcon size={14} />}
          {loadingMore
            ? m.widget_orders_loading()
            : remaining > 0
              ? m.widget_orders_show_more({ count: remaining })
              : m.widget_orders_load_more()}
        </button>
      )}
      <span aria-live="polite" role="status" style={SR_ONLY}>
        {loading
          ? m.widget_orders_loading_first()
          : loadingMore
            ? m.widget_orders_loading_status()
            : ""}
      </span>
    </section>
  );
}
